import type { Environment } from './events.js';

/**
 * sessionStorage, not localStorage. A persistent flag is the failure mode: set
 * once to verify, forgotten, and then every PR preview that person opens starts
 * capturing. Session scope survives reloads in the verification tab and dies
 * when the tab closes, which matches how long the flag is actually wanted.
 */
export const VERIFY_FLAG_KEY = 'parcel:ph-verify';

export interface InitGateInput {
  key: string | undefined;
  isProductionBuild: boolean;
  host: string;
  productionHost: string;
  verifyFlag: boolean;
  internal: boolean;
}

export type InitDecision =
  | { init: false; reason: 'no_key' | 'internal' | 'not_production' }
  | { init: true; environment: Environment; replay: boolean };

/**
 * The one rule that must never drift: the verification hatch outranks the
 * internal block, because the person who needs the hatch is an internal user.
 * It lives in one exported predicate so the gate and the web provider share it
 * instead of re-encoding the same ordering by hand.
 */
export function internalBlockApplies(input: { internal: boolean; verifyFlag: boolean }): boolean {
  return input.internal && !input.verifyFlag;
}

/** Ordered, first match wins; the hatch-vs-internal priority is the shared predicate above. */
export function resolveInitGate(input: InitGateInput): InitDecision {
  if (!input.key) return { init: false, reason: 'no_key' };

  if (internalBlockApplies(input)) return { init: false, reason: 'internal' };

  if (input.verifyFlag) {
    // Replay stays off so verification laps do not spend the 30 day replay
    // retention on developer sessions.
    return { init: true, environment: 'development', replay: false };
  }

  if (input.isProductionBuild && input.host === input.productionHost) {
    return { init: true, environment: 'production', replay: true };
  }

  return { init: false, reason: 'not_production' };
}
